import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { MessageService } from 'primeng/api';
import { forkJoin, Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { PropertyService } from '../properties/service/property.service';
import { SharedModule } from 'src/app/core/shared/shared.module';
import { TourStatus } from '../../enums/tour-statuses.enum';
import { Status } from 'src/app/core/shared/interface';
import { CrmService } from './crm.service';
import { environment } from 'src/environments/environment';
import { ColorPickerModule } from 'primeng/colorpicker';
import { MessageConversationComponent } from '../followups/message-conversation/message-conversation.component';
import { TemplateService } from '../templates/templates.service';
import { ConversationRole } from '../../constants/conversation-roles.constant';
import { ChatLogService } from '../chat-logs/chat-log.service';
import { CreateToursComponent } from '../create-tour/create-tours.component';

type SummaryKeys = 'total' | 'needsAttention' | 'waitingOnLead' | 'scheduled' | 'readyToApply' | 'signedElsewhere' | 'unresponsive' | 'stalled';

interface FilterChip {
  key: string;
  label: string;
  color: string;
  summaryKey: SummaryKeys;
  tooltip: string;
}

// Each tour status can have multiple named stages; the one with isDefault:true is shown in the table.
const DEFAULT_LEAD_STAGES = [
  { statusKey: 'Pending',        stageName: 'New Inquiry',    color: '#6c757d', isDefault: true, isSystem: true },
  { statusKey: 'Active',         stageName: 'Tour Scheduled', color: '#0d6efd', isDefault: true, isSystem: true },
  { statusKey: 'Completed',      stageName: 'Toured',         color: '#198754', isDefault: true, isSystem: true },
  { statusKey: 'Auto Cancelled', stageName: 'Auto Cancelled', color: '#dc3545', isDefault: true, isSystem: true },
  { statusKey: 'Cancelled',      stageName: 'Cancelled',      color: '#6c757d', isDefault: true, isSystem: true },
  { statusKey: 'Rescheduled',    stageName: 'Rescheduled',    color: '#432874', isDefault: true, isSystem: true },
  { statusKey: 'In_progress',    stageName: 'In Progress',    color: '#0c5460', isDefault: true, isSystem: true },
  { statusKey: 'Pre Book',       stageName: 'Pre Book',        color: '#7c3aed', isDefault: true, isSystem: true },
];

@Component({
  selector: 'app-crm',
  standalone: true,
  imports: [CommonModule, FormsModule, TableModule, ButtonModule, ToastModule, TooltipModule, DialogModule, DropdownModule, InputTextModule, SharedModule, ColorPickerModule, MessageConversationComponent, CreateToursComponent],
  templateUrl: './crm.component.html',
  styleUrls: ['./crm.component.scss'],
  providers: [MessageService],
})
export class CrmComponent implements OnInit, OnDestroy {
  groupedLeads: any[] = [];
  expandedRows: { [key: string]: boolean } = {};
  isLoadingLeads = false;
  userInfo: any = null;

  // Lead stage management
  leadStages: any[] = [];
  editableStages: any[] = [];
  showStageDialog = false;
  isSavingStages = false;
  isLoadingStages = true;

  // Chat view state
  isChatOpen = false;
  isChatLoading = false;
  isChatSystemConversation = true;
  selectedTour: any | null = null;

  showAiHelpDialog = false;
  aiHelpLead: any = null;

  // Create Tour dialog
  createTourVisible = false;

  get planNames(): any[] {
    return this.planOptions.filter(p => p.value !== null);
  }

  showCreateTourDialog(): void {
    this.createTourVisible = true;
  }

  onCreateTourClose(): void {
    this.createTourVisible = false;
    this.loadSummary();
    this.resetAndFetch();
  }

  showEditDialog = false;
  editLead: any = null;
  editFollowupStatus: string | null = null;
  editManualStatusLock = false;
  editSelectedLeadStages: string[] = [];
  isSavingStatus = false;

  // Add-stage form state
  newStageName = '';
  newStageTourStatus: string | null = null;
  newStageColor = '#7c3aed';
  newStageColorHex = '7c3aed';

  // All possible tour status display values — used in the Add Stage form dropdown
  readonly stageTourStatusOptions: { label: string; value: string }[] = [
    { label: 'Pending',        value: 'Pending' },
    { label: 'Active',         value: 'Active' },
    { label: 'Completed',      value: 'Completed' },
    { label: 'Cancelled',      value: 'Cancelled' },
    { label: 'Auto Cancelled', value: 'Auto Cancelled' },
    { label: 'Rescheduled',    value: 'Rescheduled' },
    { label: 'In Progress',    value: 'In_progress' },
    { label: 'Pre Book',    value: 'pre_book' },

  ];

  readonly stageColorSwatches: string[] = [
    '#7c3aed', '#2563eb', '#0891b2', '#16a34a',
    '#ca8a04', '#ea580c', '#dc2626', '#db2777',
    '#475569', '#1e293b', '#9333ea',
  ];

  // Followup statuses
  followupStatuses: { title: string; slug: string }[] = [];

  // Summary widgets
  summary = { total: 0, needsAttention: 0, waitingOnLead: 0, scheduled: 0, readyToApply: 0, signedElsewhere: 0, unresponsive: 0, stalled: 0 };

  // Filter chips
  activeFilter = 'all';
  readonly filterChips: FilterChip[] = [
    { key: 'all',              label: 'All Tours',        color: '#6c757d', summaryKey: 'total',           tooltip: 'Every tour across all statuses.' },
    { key: 'needs-attention',  label: 'Needs Attention',  color: '#dc3545', summaryKey: 'needsAttention',  tooltip: 'AI hit a question that needs a human.' },
    { key: 'waiting-on-lead',  label: 'Waiting on Lead',  color: '#fd7e14', summaryKey: 'waitingOnLead',   tooltip: 'AI replied; ball is in the lead\'s court.' },
    { key: 'scheduled',        label: 'Scheduled',        color: '#0d6efd', summaryKey: 'scheduled',       tooltip: 'Pre-tour leads — templates are handling the conversation.' },
    { key: 'ready-to-apply',   label: 'Ready to Apply',   color: '#198754', summaryKey: 'readyToApply',    tooltip: 'The lead appears ready for the next step.' },
    { key: 'signed-elsewhere', label: 'Signed Elsewhere', color: '#6f42c1', summaryKey: 'signedElsewhere', tooltip: 'The lead explicitly says they leased or signed somewhere else.' },
    { key: 'stalled',          label: 'Stalled',          color: '#780f0f', summaryKey: 'stalled',         tooltip: 'No interaction between lead and AI for 48+ hours.' },
    { key: 'unresponsive',     label: 'Unresponsive',     color: '#adb5bd', summaryKey: 'unresponsive',    tooltip: 'Five consecutive outreach attempts with no reply.' },
  ];

  // Column filters
  crmFilters = { search: '', planName: null as string | null, residentId: null as string | null, tourStatusFilter: null as string | null, leadStageKey: null as string | null, followupStatusFilter: null as string | null };
  planOptions: any[] = [];
  residentOptions: any[] = [];
  tourStatusOptions: any[] = [];
  leadStageOptions: any[] = [];
  followupStatusOptions: any[] = [];

  private searchSubject = new Subject<string>();
  private searchSub!: Subscription;

  private ws: WebSocket | null = null;

  constructor(
    private propertyService: PropertyService,
    private crmService: CrmService,
    private messageService: MessageService,
    private templateService: TemplateService,
    private chatLogService: ChatLogService,
  ) {}

  ngOnInit(): void {
    this.searchSub = this.searchSubject.pipe(debounceTime(400), distinctUntilChanged()).subscribe(() => this.resetAndFetch());
    this.tourStatusOptions = [
      { label: 'All Statuses', value: null },
      ...Object.values(Status).map(s => ({ label: s, value: s })),
      { label: 'Pre Book', value: 'pre_book' },
    ];
    this.propertyService.getUserInfo().subscribe(user => {
      this.userInfo = user;
      this.loadLeadStages(user.property_id);
      this.loadFollowupStatuses(user.property_id);
      this.loadSummary();
      this.loadFilterOptions(user.property_id);
      this.connectWebSocket(user.property_id);
      this.loadAllLeads();
    });
  }

  ngOnDestroy(): void {
    this.ws?.close();
    this.searchSub?.unsubscribe();
  }

  private connectWebSocket(propertyId: string): void {
    const wsBase = environment.socketServiceUrl
      .replace('https://', 'wss://')
      .replace('http://', 'ws://')
      .replace(/\/$/, '');

    this.ws = new WebSocket(`${wsBase}/events`);

    this.ws.onopen = () => {
      console.log('[CRM WS] Connected, registering for property:', propertyId);
      this.ws!.send(JSON.stringify({ event: 'register', propertyId }));
    };

    this.ws.onmessage = (event) => {
      console.log('[CRM WS] Message received:', event.data);
      const msg = JSON.parse(event.data);
      if (msg.event === 'followup_status_update') { this.applyStatusUpdate(msg.data); this.loadSummary(); }
      if (msg.event === 'tour_created') { this.applyNewTour(msg.data); this.loadSummary(); }
      if (msg.event === 'tour_updated') { this.applyTourUpdate(msg.data); this.loadSummary(); }
    };

    this.ws.onerror = (err) => {
      console.error('[CRM WS] Error:', err);
    };

    this.ws.onclose = () => {
      setTimeout(() => this.connectWebSocket(propertyId), 3000);
    };
  }

  private applyNewTour(tour: any): void {
    const flat = this.groupedLeads.flatMap(g => [
      { ...g, children: undefined },
      ...(g.children ?? []),
    ]);
    this.groupedLeads = this.groupByPhone([tour, ...flat]);
  }

  private applyTourUpdate(updated: any): void {
    const id = updated._id;
    const flat = this.groupedLeads.flatMap(g => [
      { ...g, children: undefined },
      ...(g.children ?? []),
    ]);
    const idx = flat.findIndex(r => r._id === id);
    if (idx === -1) return;
    flat[idx] = { ...flat[idx], ...updated };
    this.groupedLeads = this.groupByPhone(flat);
  }

  private applyStatusUpdate(data: { ids: string[]; followupStatus: string }): void {
    const idSet = new Set(data.ids);
    const flat = this.groupedLeads.flatMap(g => [
      { ...g, children: undefined },
      ...(g.children ?? []),
    ]);
    flat.forEach(r => { if (idSet.has(r._id)) r.followupStatus = data.followupStatus; });
    this.groupedLeads = this.groupByPhone(flat);
  }

  loadSummary(): void {
    this.propertyService.getCrmSummary().subscribe({
      next: (res) => { this.summary = res; },
      error: () => {},
    });
  }

  loadFollowupStatuses(propertyId: string): void {
    this.crmService.getFollowupStatuses(propertyId).subscribe({
      next: (res) => {
        this.followupStatuses = res?.statuses ?? [];
        this.followupStatusOptions = [
          { label: 'All', value: null },
          ...this.followupStatuses.map(s => ({ label: s.title, value: s.slug })),
        ];
      },
      error: () => {
        console.error('Error fetching followup statuses');
      },
    });
  }

  getFollowupStatusTitle(slug: string): string {
    if (!slug) return '-';
    const match = this.followupStatuses.find(s => s.slug === slug);
    return match ? match.title : slug;
  }

  loadLeadStages(propertyId: string): void {
    this.isLoadingStages = true;
    this.crmService.getLeadStages(propertyId).subscribe({
      next: (res) => {
        this.leadStages = res?.stages?.length ? res.stages : [...DEFAULT_LEAD_STAGES];
        this.buildLeadStageOptions();
        this.isLoadingStages = false;
      },
      error: () => {
        this.leadStages = [...DEFAULT_LEAD_STAGES];
        this.buildLeadStageOptions();
        this.isLoadingStages = false;
      },
    });
  }

  private buildLeadStageOptions(): void {
    // Use only the default stage per tour status for the filter dropdown
    const seen = new Set<string>();
    const options: any[] = [{ label: 'All Stages', value: null }];
    for (const s of this.leadStages) {
      if (s.isDefault && !seen.has(s.statusKey)) {
        seen.add(s.statusKey);
        options.push({ label: s.stageName, value: s.statusKey });
      }
    }
    this.leadStageOptions = options;
  }

  openAiHelpDialog(lead: any): void {
    this.aiHelpLead = lead;
    this.showAiHelpDialog = true;
  }

  onClickInfoButton(tour: any): void {
    this.selectedTour = { ...tour, messages: [] };
    this.isChatOpen = true;
    this.isChatLoading = true;
    this.isChatSystemConversation = true;
    this.templateService.getSystemConversation(tour._id, ConversationRole.SYSTEM, ConversationRole.RESIDENT).subscribe({
      next: (response) => {
        if (response?.tour) {
          if (response?.auditLogs || response?.auditLogsReschedule) {
            this.selectedTour = { ...response.auditLogsReschedule, ...response.auditLogs[0], ...response.tour, messages: response.conversations };
          } else {
            this.selectedTour = { ...response.tour, messages: response.conversations };
          }
        } else {
          this.selectedTour.messages = response.conversations;
        }
        this.isChatLoading = false;
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load messages.' });
        this.isChatLoading = false;
      },
      complete: () => { this.isChatLoading = false; },
    });
  }

  fetchSpecificConversationNew(event: { conversationFrom: string; conversationTo: string }): void {
    this.isChatLoading = true;

    const roles = [event.conversationFrom, event.conversationTo];
    const hasSystem  = roles.includes(ConversationRole.SYSTEM);
    const hasResident = roles.includes(ConversationRole.RESIDENT);
    const hasVisitor  = roles.includes(ConversationRole.VISITOR);

    this.isChatSystemConversation = false;
    if ((hasSystem && hasResident && !hasVisitor) || (hasSystem && hasVisitor)) {
      this.isChatSystemConversation = true;
    }

    if (hasSystem && hasVisitor && !hasResident) {
      forkJoin({
        twilio: this.templateService.getSystemConversation(this.selectedTour._id, event.conversationFrom, event.conversationTo),
        chatbot: this.chatLogService.getLogsByPhone(this.selectedTour.phone_number),
      }).subscribe({
        next: ({ twilio, chatbot }) => {
          const twilioMsgs: any[] = twilio?.conversations ?? [];
          const chatbotMsgs = (chatbot?.history ?? []).map((msg: any) => ({
            message: msg.content,
            fromRole: msg.type === 'ai' ? ConversationRole.SYSTEM : ConversationRole.VISITOR,
            createdAt: msg.time,
            scheduleNumber: 1,
            isTextbot: msg.type === 'ai',
          }));
          const allMessages = [...twilioMsgs, ...chatbotMsgs]
            .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
          if (twilio?.tour) {
            this.selectedTour = { ...(twilio.auditLogsReschedule ?? {}), ...(twilio.auditLogs?.[0] ?? {}), ...twilio.tour, messages: allMessages };
          } else {
            this.selectedTour = { ...this.selectedTour, messages: allMessages };
          }
          this.isChatLoading = false;
        },
        error: () => {
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load messages.' });
          this.isChatLoading = false;
        },
      });
      return;
    }

    this.templateService.getSystemConversation(this.selectedTour._id, event.conversationFrom, event.conversationTo).subscribe({
      next: (response) => {
        if (response?.tour) {
          if (response?.auditLogs || response?.auditLogsReschedule) {
            this.selectedTour = { ...response.auditLogsReschedule, ...response.auditLogs[0], ...response.tour, messages: response.conversations };
          } else {
            this.selectedTour = { ...response.tour, messages: response.conversations };
          }
        } else {
          this.selectedTour.messages = response.conversations;
        }
        this.isChatLoading = false;
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load messages.' });
        this.isChatLoading = false;
      },
      complete: () => { this.isChatLoading = false; },
    });
  }

  openEditDialog(lead: any): void {
    this.editLead = lead;
    this.editFollowupStatus = lead.followupStatus ?? null;
    this.editManualStatusLock = lead.manualStatusLock ?? false;
    this.editSelectedLeadStages = lead.selectedLeadStages ? [...lead.selectedLeadStages] : [];
    this.showEditDialog = true;
  }

  get editLeadStageChips(): { stageName: string; color: string; selected: boolean }[] {
    if (!this.editLead) return [];
    const key = this.tourStatus(this.editLead).text;
    return this.leadStages
      .filter(s => s.statusKey === key)
      .map(s => ({ stageName: s.stageName, color: s.color, selected: this.editSelectedLeadStages.includes(s.stageName) }));
  }

  toggleEditLeadStage(stageName: string): void {
    const idx = this.editSelectedLeadStages.indexOf(stageName);
    if (idx === -1) this.editSelectedLeadStages.push(stageName);
    else this.editSelectedLeadStages.splice(idx, 1);
  }

  getLeadStageChips(lead: any): { stageName: string; color: string }[] {
    if (!lead.selectedLeadStages?.length) return [];
    return lead.selectedLeadStages.map((name: string) => {
      const stage = this.leadStages.find(s => s.stageName === name);
      return { stageName: name, color: stage?.color ?? '#6c757d' };
    });
  }

  saveFollowupStatus(): void {
    if (!this.editLead || !this.editFollowupStatus) return;
    this.isSavingStatus = true;
    this.crmService.updateFollowupStatus(this.editLead._id, this.editFollowupStatus, this.editManualStatusLock, this.editSelectedLeadStages).subscribe({
      next: (updated) => {
        this.isSavingStatus = false;
        this.showEditDialog = false;
        this.applyTourUpdate(updated);
        this.loadSummary();
        this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Followup status updated' });
      },
      error: () => {
        this.isSavingStatus = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to update status' });
      },
    });
  }

  openStageDialog(): void {
    this.editableStages = this.leadStages.map(s => ({ ...s }));
    this.newStageName = '';
    this.newStageTourStatus = null;
    this.newStageColor = '#7c3aed';
    this.newStageColorHex = '7c3aed';
    this.showStageDialog = true;
  }

  saveStages(): void {
    this.isSavingStages = true;
    this.crmService.saveLeadStages(this.userInfo.property_id, this.editableStages).subscribe({
      next: () => {
        this.leadStages = this.editableStages.map(s => ({ ...s }));
        this.buildLeadStageOptions();
        this.showStageDialog = false;
        this.isSavingStages = false;
        this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Lead stages updated' });
      },
      error: () => {
        this.isSavingStages = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to save lead stages' });
      },
    });
  }

  // Returns the default stage for a lead's current tour status
  getLeadStage(lead: any): { stageName: string; color: string } | null {
    const key = this.tourStatus(lead).text;
    return (
      this.leadStages.find(s => s.statusKey === key && s.isDefault) ||
      this.leadStages.find(s => s.statusKey === key) ||
      null
    );
  }

  // Groups editableStages by statusKey for the dialog view
  get groupedEditableStages(): { statusKey: string; stages: any[] }[] {
    const map = new Map<string, any[]>();
    for (const stage of this.editableStages) {
      if (!map.has(stage.statusKey)) map.set(stage.statusKey, []);
      map.get(stage.statusKey)!.push(stage);
    }
    return Array.from(map.entries()).map(([statusKey, stages]) => ({ statusKey, stages }));
  }

  addCustomStage(): void {
    if (!this.newStageName.trim() || !this.newStageTourStatus) return;
    this.editableStages.push({
      statusKey: this.newStageTourStatus,
      stageName: this.newStageName.trim(),
      color: this.newStageColor,
      isDefault: false,
      isSystem: false,
    });
    this.newStageName = '';
    this.newStageTourStatus = null;
    this.newStageColor = '#7c3aed';
    this.newStageColorHex = '7c3aed';
  }

  removeEditableStage(statusKey: string, stageName: string): void {
    const inUse = this.groupedLeads.some(lead =>
      [lead, ...(lead.children ?? [])].some((r: any) => r.selectedLeadStages?.includes(stageName))
    );
    if (inUse) {
      this.messageService.add({ severity: 'warn', summary: 'Stage in use', detail: `"${stageName}" is assigned to one or more leads. Remove it from those leads first.` });
      return;
    }
    const idx = this.editableStages.findIndex(s => s.statusKey === statusKey && s.stageName === stageName);
    if (idx === -1) return;
    this.editableStages.splice(idx, 1);
  }

  chipsTooltip(chips: { stageName: string }[]): string {
    return chips.slice(1).map(c => c.stageName).join(', ');
  }

  onColorPickerChange(event: any): void {
    const hex = typeof event.value === 'string' ? event.value : event.value?.hex ?? event.value;
    this.newStageColor = '#' + hex.replace('#', '');
    this.newStageColorHex = hex.replace('#', '');
  }

  onChipClick(key: string): void {
    this.activeFilter = key;
    this.crmFilters.followupStatusFilter = null;
    this.loadAllLeads();
  }

  loadFilterOptions(propertyId: string): void {
    this.propertyService.getVisibleUnits(propertyId, { limit: 1000 }).subscribe({
      next: (res) => {
        this.planOptions = [
          { label: 'All Plans', value: null },
          ...res.data.map((p: any) => ({ label: p.unit, value: p.unit })),
        ];
      },
    });
    this.propertyService.getTenants({}).subscribe({
      next: (res) => {
        this.residentOptions = [
          { label: 'All Residents', value: null },
          ...res.data.map((t: any) => ({ label: `${t.first_name} ${t.last_name}`, value: t._id })),
          { label: 'To Be Assigned', value: 'unassigned' },
        ];
      },
    });
  }

  onSearchInput(): void {
    this.searchSubject.next(this.crmFilters.search);
  }

  onDropdownChange(): void {
    this.resetAndFetch();
  }

  onFollowupStatusDropdownChange(): void {
    if (this.crmFilters.followupStatusFilter) this.activeFilter = 'all';
    this.resetAndFetch();
  }

  resetAndFetch(): void {
    this.loadAllLeads();
  }

  loadAllLeads(): void {
    this.isLoadingLeads = true;
    this.propertyService.getCrmFollowups({
      page: 1,
      limit: 10000,
      filters: JSON.stringify({ followupFilter: this.activeFilter, ...this.crmFilters }),
    }).subscribe({
      next: (response) => {
        this.groupedLeads = this.groupByPhone(response.data);
        this.isLoadingLeads = false;
      },
      error: () => {
        console.error('Error fetching CRM leads');
        this.isLoadingLeads = false;
      },
    });
  }

  private groupByPhone(data: any[]): any[] {
    const groups = new Map<string, any[]>();
    const now = Date.now();

    for (const lead of data) {
      const key = lead.phone_number || 'unknown';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(lead);
    }

    const result: any[] = [];

    groups.forEach((entries) => {
      const aiNeedsHelp = entries.filter(e => e.followupStatus === 'ai-needs-help');
      const rest = entries.filter(e => e.followupStatus !== 'ai-needs-help');

      const upcoming = rest
        .filter(e => this.toTimestamp(e) >= now)
        .sort((a, b) => this.toTimestamp(a) - this.toTimestamp(b));

      const past = rest
        .filter(e => this.toTimestamp(e) < now)
        .sort((a, b) => this.toTimestamp(b) - this.toTimestamp(a));

      const [parent, ...children] = [...aiNeedsHelp, ...upcoming, ...past];
      result.push({ ...parent, children });
    });

    result.sort((a, b) => {
      const aAnh = a.followupStatus === 'ai-needs-help';
      const bAnh = b.followupStatus === 'ai-needs-help';
      if (aAnh !== bAnh) return aAnh ? -1 : 1;

      const aTs = this.toTimestamp(a);
      const bTs = this.toTimestamp(b);
      const aUpcoming = aTs >= now;
      const bUpcoming = bTs >= now;
      if (aUpcoming && bUpcoming) return aTs - bTs;
      if (aUpcoming) return -1;
      if (bUpcoming) return 1;
      return bTs - aTs;
    });

    return result;
  }

  private toTimestamp(lead: any): number {
    if (lead.planned_date) {
      const ms = Date.parse(lead.planned_date.replace(' ', 'T') + ':00Z');
      if (!isNaN(ms)) return ms;
    }
    return 0;
  }

  tourStatus(lead: any): { text: string; class: string } {
    const status = lead?.status;
    const tourStatus = lead?.tourStatus;

    if (tourStatus === TourStatus.PRE_BOOK) return { text: 'Pre Book', class: 'Pre_book' };
    if (tourStatus === TourStatus.RESCHEDULED) return this.formatStatus(TourStatus.RESCHEDULED);

    if (
      (status === TourStatus.ACTIVE && tourStatus === TourStatus.IN_PROGRESS) ||
      (status === TourStatus.PENDING && tourStatus !== TourStatus.CANCELLED)
    ) {
      return this.formatStatus(status);
    }

    const appendText = status === TourStatus.PENDING && tourStatus === TourStatus.CANCELLED ? 'Auto ' : '';
    return this.formatStatus(tourStatus, appendText);
  }

  formatStatus(value: string, appendText = ''): { text: string; class: string } {
    if (!value) return { text: '-', class: '' };
    const formatted = value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
    return { text: appendText ? appendText + formatted : formatted, class: formatted };
  }
}
